import { useEffect, useContext, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  RefreshCcw,
  Save,
  ChevronDown,
  ChevronUp,
  Filter,
  FileSpreadsheet,
} from "lucide-react";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BASE_URL = "https://nissi-india.com";

const aspxSelect = (v) => {
  const s = (v ?? "").toString().trim();
  return s === "" ? "Select" : s;
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pad2 = (n) => String(n).padStart(2, "0");
const toDDMMYYYY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDDMMYYYY = () => toDDMMYYYY(new Date());

const parseDDMMYYYY = (s) => {
  const m = String(s || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

const getRowEmpId = (r) => safeNum(r?.fld_Employeeid ?? r?.fld_employeeid ?? r?.Employeeid ?? r?.employeeId);
const getRowEmpCode = (r) =>
  String(r?.fld_Employeecode ?? r?.fld_employeecode ?? r?.Employeecode ?? r?.empCode ?? "").trim();
const getRowEmpName = (r) =>
  String(r?.fld_Employee ?? r?.fld_employee ?? r?.Employee ?? r?.empname ?? r?.empName ?? "-").trim();
const getRowTypeName = (r) => String(r?.fld_typename ?? r?.TypeName ?? r?.typename ?? "").trim();
const getRowDesigName = (r) =>
  String(r?.fld_designationname ?? r?.DesignationName ?? r?.designame ?? r?.desigName ?? "").trim();
const getRowDeptId = (r) => String(r?.fld_deptid ?? r?.DeptId ?? r?.deptId ?? "").trim();
const getRowDesigId = (r) => String(r?.fld_desigid ?? r?.DesigId ?? r?.desigId ?? "").trim();
const getRowBata = (r) => Number(r?.fld_dailybata ?? r?.fld_bata ?? r?.Bata ?? r?.bata ?? 0);
const getRowCLA = (r) =>
  String(r?.fld_CLAallowance ?? r?.fld_CLAalllowance ?? r?.CLAAllowance ?? r?.claAllowance ?? "0").trim();
const getRowLeaveOB = (r) => Number(r?.fld_totleaveob ?? r?.fld_totleaveOB ?? 0);

const STATUS = [
  { key: "PRESENT", label: "Present" },
  { key: "HALF_PRESENT", label: "Half" },
  { key: "LEAVE", label: "Leave" },
  { key: "ABSENT", label: "Absent" },
  { key: "COMPULSORY_LOP", label: "Comp LOP" },
  { key: "HOLIDAY", label: "Holiday" },
  { key: "MGMT30", label: "Mgmt" },
  { key: "MGMT100", label: "Mgmt 100%" },
  { key: "CAMP", label: "Camp" },
];

const DailyAttendanceEntry = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const userId = Number(localStorage.getItem("userId") || 0);
  const branchType = (localStorage.getItem("branchType") || "HO").toString().trim();
  const loginBranchId = (localStorage.getItem("branchId") || "").toString().trim();

  const screenBadge = "INFRA SCREEN : C-16";
  const [showFilters, setShowFilters] = useState(true);

  const [halfDayEnabled, setHalfDayEnabled] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState([]);

  const [branchId, setBranchId] = useState(() =>
    loginBranchId && loginBranchId !== "Select" ? loginBranchId : ""
  );
  const [deptId, setDeptId] = useState("");
  const [dateText, setDateText] = useState(() => todayDDMMYYYY());

  const [year, setYear] = useState(() => String(new Date().getFullYear()));
  const [monthName, setMonthName] = useState(() =>
    new Date().toLocaleString("en-US", { month: "long" })
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [rowState, setRowState] = useState({});

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark ? "border-white/10 text-white hover:bg-white/10" : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const successBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (loading ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const ensureFinYear = () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const getHeaders = () => ({
    "x-loginuserid": String(userId || 0),
    "x-branchid": String(branchId || loginBranchId || ""),
    "x-branchtype": String(branchType || "HO"),
  });

  const loadMeta = async () => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance-entry/meta`, {
        params: { finYearId },
        headers: getHeaders(),
      });

      const ok = !!(res?.data?.success ?? res?.data?.status);
      if (ok) {
        setHalfDayEnabled(!!(res.data.halfDayEnabled ?? res.data.halfdayEnabled));
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setYears(res.data.years || []);

        const def = (localStorage.getItem("branchId") || "").toString().trim();
        const effective = def && def !== "Select" ? def : res.data.defaultBranchId;
        if (effective && effective !== "Select") {
          setBranchId(String(effective));
          localStorage.setItem("branchId", String(effective));
        }
      } else {
        toast.error(res?.data?.message || "Meta load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const normalizeRowStateFromRows = (list) => {
    const next = {};
    for (const r of list) {
      const empId = getRowEmpId(r);
      if (!empId) continue;
      next[empId] = {
        selected: false,
        statusKey: "",
        inTime: "",
        inAmPm: "AM",
        outTime: "",
        outAmPm: "PM",
        ot: "0",
      };
    }
    setRowState(next);
    setSelectedEmpIds([]);
    setSelectAll(false);
  };

  const searchGrid = async () => {
    if (!ensureFinYear()) return;

    const bId = (branchId || "").toString().trim();
    const dTxt = (dateText || "").toString().trim();
    const dep = (deptId || "").toString().trim();

    if (!bId || bId === "Select") return toast.warn("Please Select Branch...");
    if (!dTxt) return toast.warn("Please Select Date...");

    const headers = getHeaders();
    const url = `${BASE_URL}/api/payroll/daily-attendance-entry/search`;
    const params = { finYearId };
    const payload = {
      branchId: aspxSelect(bId),
      dateText: dTxt,
      departmentId: aspxSelect(dep),
    };

    console.log("===== DAILY ATTENDANCE SEARCH =====");
    console.log("URL:", url);
    console.log("PARAMS:", params);
    console.log("HEADERS:", headers);
    console.log("PAYLOAD:", payload);
    console.log("===================================");

    try {
      setLoading(true);

      const res = await axios.post(url, payload, { params, headers });

      console.log("✅ RESPONSE STATUS:", res?.status);
      console.log("✅ RESPONSE DATA:", res?.data);

      const ok = !!(res?.data?.success ?? res?.data?.status);

      if (ok) {
        const list = res?.data?.rows || res?.data?.data || [];
        setRows(Array.isArray(list) ? list : []);
        setHalfDayEnabled(!!(res?.data?.halfDayEnabled ?? res?.data?.halfdayEnabled));
        normalizeRowStateFromRows(Array.isArray(list) ? list : []);
        toast.success(`Loaded ${Array.isArray(list) ? list.length : 0} employee(s)`);
      } else {
        toast.error(res?.data?.message || "Search failed");
        setRows([]);
        normalizeRowStateFromRows([]);
      }
    } catch (err) {
      console.error("❌ SEARCH ERROR:", err);
      console.error("❌ ERROR RESPONSE:", err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.message || err?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (empId) => {
    setRowState((prev) => {
      const cur = prev[empId] || {};
      return { ...prev, [empId]: { ...cur, selected: !cur.selected } };
    });
    setSelectedEmpIds((prev) =>
      prev.includes(empId) ? prev.filter((x) => x !== empId) : [...prev, empId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedEmpIds([]);
      setRowState((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => (next[k] = { ...next[k], selected: false }));
        return next;
      });
      return;
    }

    const ids = rows.map((r) => getRowEmpId(r)).filter(Boolean);
    setSelectAll(true);
    setSelectedEmpIds(ids);
    setRowState((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = { ...(next[id] || {}), selected: true };
      });
      return next;
    });
  };

  const setOneStatus = (empId, statusKey) => {
    setRowState((prev) => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), statusKey },
    }));
  };

  const updateRowField = (empId, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [field]: value },
    }));
  };

  const validateBeforeSubmit = () => {
    if (!branchId || branchId === "Select") {
      toast.warn("Please Select Branch...");
      return false;
    }
    if (!dateText) {
      toast.warn("Please Select Date...");
      return false;
    }
    if (selectedEmpIds.length === 0) {
      toast.warn("Please select atleast one");
      return false;
    }

    const missing = selectedEmpIds.filter((id) => !String(rowState?.[id]?.statusKey || "").trim());
    if (missing.length > 0) {
      toast.warn("Please select status for all selected rows");
      return false;
    }

    const hasHalf = selectedEmpIds.some((id) => rowState?.[id]?.statusKey === "HALF_PRESENT");
    if (hasHalf && !halfDayEnabled) {
      toast.warn("Half-day is disabled for this branch.");
      return false;
    }

    return true;
  };

  const submitAttendance = async () => {
    if (!ensureFinYear()) return;
    if (!validateBeforeSubmit()) return;

    try {
      setLoading(true);

      const selectedRows = rows.filter((r) => selectedEmpIds.includes(getRowEmpId(r)));

      const payloadRows = selectedRows.map((r) => {
        const empId = getRowEmpId(r);
        const st = rowState[empId] || {};

        return {
          employeeId: empId,
          employeeCode: getRowEmpCode(r),
          employeeName: getRowEmpName(r),
          typeName: getRowTypeName(r),
          designationName: getRowDesigName(r),
          deptId: getRowDeptId(r),
          desigId: getRowDesigId(r),
          dailyBata: getRowBata(r),
          claAllowance: getRowCLA(r),
          inTime: st.inTime || "",
          inAmPm: st.inAmPm || "AM",
          outTime: st.outTime || "",
          outAmPm: st.outAmPm || "PM",
          ot: String(st.ot ?? "0"),
          statusKey: st.statusKey,
        };
      });

      const res = await axios.post(
        `${BASE_URL}/api/payroll/daily-attendance-entry/submit`,
        { branchId: aspxSelect(branchId), dateText, rowsSelected: payloadRows },
        { params: { finYearId }, headers: getHeaders() }
      );

      const ok = !!(res?.data?.success ?? res?.data?.status);
      if (ok) {
        toast.success(res?.data?.message || "Saved Successfully..");
        navigate("/PayRoll/DailyAttendanceListing");
      } else {
        toast.error(res?.data?.message || "Submit failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!rows || rows.length === 0) return toast.warn("No data to export");

    const data = rows.map((r, idx) => {
      const empId = getRowEmpId(r);
      const st = rowState?.[empId] || {};
      const statusLabel = STATUS.find((x) => x.key === st.statusKey)?.label || "";

      return {
        "S.No": idx + 1,
        "Emp Code": getRowEmpCode(r),
        Name: getRowEmpName(r),
        Type: getRowTypeName(r),
        Designation: getRowDesigName(r),
        "Leave OB": getRowLeaveOB(r),
        Status: statusLabel,
        "In Time": st.inTime ? `${st.inTime} ${st.inAmPm || ""}`.trim() : "",
        "Out Time": st.outTime ? `${st.outTime} ${st.outAmPm || ""}`.trim() : "",
        OT: String(st.ot ?? "0"),
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyAttendance");

    const now = new Date();
    const fileName = `Daily_Attendance_${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(
      now.getHours()
    )}-${pad2(now.getMinutes())}.xlsx`;

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), fileName);
    toast.success("Excel downloaded");
  };

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    const d = parseDDMMYYYY(dateText);
    if (!d) return;
    setYear(String(d.getFullYear()));
    setMonthName(d.toLocaleString("en-US", { month: "long" }));
  }, [dateText]);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top header */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>
              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Daily Attendance Entry
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search employees, mark status, then Submit.
                </div>
              </div>
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Rows
              </div>
              {halfDayEnabled && (
                <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"}`}>
                  Half-Day Enabled
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={loadMeta} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reload Masters
              </button>

              <button onClick={exportToExcel} className={ghostBtn} disabled={loading || rows.length === 0}>
                <FileSpreadsheet className="w-4 h-4" />
                Export Excel
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center gap-2 mb-4">
                <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
                <div className="text-lg font-extrabold">Filters</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={labelBase}>Branch</label>
                  <select
                    className={inputBase}
                    value={branchId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBranchId(v);
                      localStorage.setItem("branchId", String(v || ""));
                    }}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => (
                      <option
                        key={idx}
                        value={b?.fld_branchid ?? b?.fld_Branchid ?? b?.branchId ?? b?.BranchId ?? ""}
                      >
                        {b?.fld_branchname ?? b?.fld_BranchName ?? b?.branchName ?? b?.BranchName ?? "Branch"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Department</label>
                  <select className={inputBase} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                    <option value="">Select</option>
                    {departments.map((d, idx) => (
                      <option key={idx} value={d?.fld_deptid ?? d?.deptId ?? d?.DeptId ?? ""}>
                        {d?.fld_departmentname ?? d?.deptName ?? d?.DeptName ?? "Department"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Date (dd/MM/yyyy)</label>
                  <input
                    className={inputBase}
                    value={dateText}
                    onChange={(e) => setDateText(e.target.value)}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={labelBase}>Year</label>
                  <input className={inputBase} value={year} readOnly />
                </div>

                <div>
                  <label className={labelBase}>Month</label>
                  <input className={inputBase} value={monthName} readOnly />
                </div>

                {/* Search + Submit buttons inside Filters */}
                <div className="md:col-span-4 flex flex-wrap items-center gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      console.log("✅ Search button clicked");
                      searchGrid();
                    }}
                    className={primaryBtn}
                  >
                    <Search className="w-4 h-4" />
                    Search
                  </button>

                  <button
                    onClick={submitAttendance}
                    className={successBtn}
                    disabled={loading || selectedEmpIds.length === 0}
                  >
                    <Save className="w-4 h-4" />
                    Submit
                  </button>

                  <div
                    className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                      isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    Selected: {selectedEmpIds.length}
                  </div>

                  <div className="text-sm opacity-70">
                    Select checkbox + pick status for each selected row.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="min-w-[1400px] w-full table-fixed">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="w-[60px] px-3 py-4 text-center">S.No</th>
                    <th className="w-[70px] px-3 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        Select
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                    <th className="w-[110px] px-3 py-4 text-center">Emp Code</th>
                    <th className="w-[180px] px-3 py-4 text-center">Name</th>
                    <th className="w-[110px] px-3 py-4 text-center">Type</th>
                    <th className="w-[180px] px-3 py-4 text-center">Designation</th>
                    <th className="w-[90px] px-3 py-4 text-center">Leave OB</th>
                    <th className="w-[150px] px-3 py-4 text-center">Status</th>
                    <th className="w-[160px] px-3 py-4 text-center">In</th>
                    <th className="w-[160px] px-3 py-4 text-center">Out</th>
                    <th className="w-[80px] px-3 py-4 text-center">OT</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 11 }).map((__, c) => (
                          <td key={c} className="px-3 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empId = getRowEmpId(r);
                      const st = rowState?.[empId] || {};
                      const disabled = !st.selected;

                      return (
                        <tr key={empId || idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
                          <td className="px-3 py-4 text-center">
                            <input type="checkbox" checked={!!st.selected} onChange={() => toggleSelect(empId)} className="w-4 h-4" />
                          </td>
                          <td className={`px-3 py-4 text-sm text-center font-bold ${isDark ? "text-sky-300" : "text-blue-700"}`}>
                            {getRowEmpCode(r) || "-"}
                          </td>
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getRowEmpName(r)}</td>
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getRowTypeName(r)}</td>
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{getRowDesigName(r)}</td>
                          <td className={`px-3 py-4 text-sm text-center font-bold ${isDark ? "text-amber-200" : "text-amber-700"}`}>
                            {getRowLeaveOB(r)}
                          </td>
                          <td className="px-3 py-4 text-center">
                            <select
                              className={`${inputBase} py-2`}
                              value={st.statusKey || ""}
                              onChange={(e) => setOneStatus(empId, e.target.value)}
                              disabled={disabled}
                            >
                              <option value="">Select</option>
                              {STATUS.map((s) => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                              ))}
                            </select>
                            {!halfDayEnabled && st.statusKey === "HALF_PRESENT" && (
                              <div className="text-xs mt-1 text-red-400 font-bold">Half-day disabled</div>
                            )}
                          </td>
                          <td className="px-3 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <input
                                className={`${inputBase} py-2 w-[80px] font-mono text-center`}
                                value={st.inTime || ""}
                                onChange={(e) => updateRowField(empId, "inTime", e.target.value)}
                                placeholder="hh:mm"
                                disabled={disabled}
                              />
                              <select
                                className={`${inputBase} py-2 w-[70px] text-center`}
                                value={st.inAmPm || "AM"}
                                onChange={(e) => updateRowField(empId, "inAmPm", e.target.value)}
                                disabled={disabled}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <input
                                className={`${inputBase} py-2 w-[80px] font-mono text-center`}
                                value={st.outTime || ""}
                                onChange={(e) => updateRowField(empId, "outTime", e.target.value)}
                                placeholder="hh:mm"
                                disabled={disabled}
                              />
                              <select
                                className={`${inputBase} py-2 w-[70px] text-center`}
                                value={st.outAmPm || "PM"}
                                onChange={(e) => updateRowField(empId, "outAmPm", e.target.value)}
                                disabled={disabled}
                              >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                              </select>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-center">
                            <input
                              className={`${inputBase} py-2 w-[60px] text-center font-bold`}
                              value={String(st.ot ?? "0")}
                              onChange={(e) => updateRowField(empId, "ot", e.target.value)}
                              disabled={disabled}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 text-sm opacity-70">
            Tip: Use <b>Select</b> checkbox first, then choose <b>Status</b> and timings.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DailyAttendanceEntry;